import "@/App.css";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import JoinCreate from "@/pages/JoinCreate";
import MusicBuyASong from "@/pages/MusicBuyASong";
import ShopNSavePWA from "@/pages/ShopNSavePWA";
import MusicVideoStudio from "@/pages/MusicVideoStudio";
import MessengerApp from "@/pages/MessengerApp";
import PlaySpace from "@/game/space/PlaySpace";
import { SessionProvider, useSession } from "@/context/SessionContext";
import { BillingGateProvider } from "@/context/BillingGate";
import { PlayerProvider } from "@/context/PlayerContext";
import { WorkspaceProvider, useWorkspace } from "@/context/WorkspaceContext";
import AppShell from "@/components/shell/AppShell";
import MiniPlayer from "@/components/mist/MiniPlayer";
import RateLimitBanner from "@/components/mist/RateLimitBanner";
import TopUpGate from "@/components/billing/TopUpGate";
import JimCompanion from "@/components/dashboard/JimCompanion";
import PaymentReturn from "@/components/billing/PaymentReturn";
import AdvertisePortal from "@/components/ads/AdvertisePortal";
import FounderAdConsole from "@/components/ads/FounderAdConsole";
import DesktopDashboard from "@/components/shell/DesktopDashboard";
import UserCommandCentre from "@/components/shell/UserCommandCentre";
import DesktopNavRail from "@/components/shell/DesktopNavRail";
import VerifyReport from "@/components/compliance/VerifyReport";
import GalleryVault from "@/components/gallery/GalleryVault";
import InstallAppPrompt from "@/components/pwa/InstallAppPrompt";
import RenderDock from "@/components/pipelines/RenderDock";
import GameLocked from "@/components/shell/GameLocked";

const FOUNDER_EMAIL = "djohnm1972@gmail.com";
const FOUNDER_HANDLE = "johnnywalker";

/* Root layout — lives INSIDE the SessionProvider so it can read the signed-in
   user tier. The desktop "Control Deck" (DesktopDashboard) is restricted to
   System Administrators + Tier-3 Founders. For everyone else the Galaxy S20
   chassis stays perfectly centered on the desktop canvas. */
function RootShell() {
  const { user } = useSession();
  const { activeWorkspaceView, shell } = useWorkspace();
  const { pathname } = useLocation();

  // LOCKED MiST game route — rendered standalone (no device chassis, no desktop
  // nav rail, no Misteck UI). The in-development Road Shooter / Space module is
  // founder-only; everyone else gets an Access Denied screen + redirect home.
  const isFounderUser =
    ["founder", "admin"].includes(user?.role) ||
    (user?.handle || "").toLowerCase() === FOUNDER_HANDLE ||
    (user?.email || "").toLowerCase() === FOUNDER_EMAIL;
  if (pathname === "/play/space") return isFounderUser ? <PlaySpace /> : <GameLocked />;

  const isAdminOrFounder =
    ["founder", "admin"].includes(user?.role) ||
    (user?.handle || "").toLowerCase() === FOUNDER_HANDLE ||
    (user?.email || "").toLowerCase() === FOUNDER_EMAIL;
  const isTier3 = (user?.subscription_tier || "") === "tier_3";
  const shouldShowControlDeck = !!user && (isAdminOrFounder || isTier3);
  // The 2/3 Control Deck shows whenever the active workspace pane is the Home
  // Deck. Studios/hubs portal an OPAQUE pane ABOVE it, so the deck simply stays
  // mounted underneath — NO fragile URL-derived guard. (In-app pushState is
  // invisible to react-router's useLocation, which previously left the deck
  // stuck hidden after returning from a studio/hub until a hard refresh.)
  const deckVisible = shouldShowControlDeck && activeWorkspaceView === "CONTROL_DECK";
  const centreVisible = !!shell?.authed && !shouldShowControlDeck && activeWorkspaceView === "CONTROL_DECK";

  // Base phone-frame styling. AUTHED → framed phone anchored right of the nav
  // rail. UNAUTHED (landing/gateway) → on desktop the chassis goes FULL-SCREEN
  // (no phone frame) so Gateway can render its own split layout (mobile wrapper
  // left + landing showcase right). Below md it stays a centered mobile column.
  const isAuthed = !!shell?.authed;
  const chassisBase = !isAuthed
    ? "w-full max-w-[412px] h-[100dvh] mx-auto bg-cream text-charcoal relative flex flex-col overflow-hidden shadow-2xl border-x border-charcoal/15 md:max-w-none md:w-full md:h-[100dvh] md:mx-0 md:rounded-none md:border-0 md:shadow-none"
    : "w-full max-w-[412px] h-[100dvh] mx-auto bg-cream text-charcoal relative flex flex-col overflow-hidden shadow-2xl border-x border-charcoal/15 md:max-w-none md:w-[412px] md:h-[844px] md:mx-0 md:rounded-[36px] md:border-8 md:border-black md:shadow-[8px_8px_0px_0px_#000] md:overflow-hidden";
  const chassisAnchor = !isAuthed
    ? ""
    : "md:fixed md:left-[96px] md:top-1/2 md:-translate-y-1/2";

  return (
    <>
      {/* Permanent left navigation rail — DESKTOP ONLY (md:+). Rendered as a
          RootShell sibling so its fixed positioning is viewport-relative (the
          chassis uses a transform, which would otherwise trap a fixed child). */}
      {shell?.authed && (
        <DesktopNavRail
          view={shell.view}
          setView={shell.navigate}
          onCreate={shell.openCreate}
          onLogout={shell.logout}
          socialUnread={shell.socialUnread}
        />
      )}

      {/* Galaxy S20 device chassis — full-width mobile app on <md, framed phone
          on desktop (md), anchored right of the nav rail. Proportions preserved. */}
      <div className={`${chassisBase} ${chassisAnchor}`} data-testid="device-chassis">
        <Routes>
          <Route path="/jim-companion" element={<JimCompanion />} />
          <Route path="/verify/:token" element={<VerifyReport />} />
          <Route path="/gallery" element={<GalleryVault />} />
          <Route path="/join" element={<JoinCreate />} />
          <Route path="/music/buyasong" element={<MusicBuyASong />} />
          <Route path="/shopnsave/pwa" element={<ShopNSavePWA />} />
          <Route path="/music/video-studio" element={<MusicVideoStudio />} />
          <Route path="/messenger" element={<MessengerApp />} />
          <Route path="/payment/success" element={<PaymentReturn mode="success" />} />
          <Route path="/payment/cancel" element={<PaymentReturn mode="cancel" />} />
          <Route path="/advertise" element={<AdvertisePortal />} />
          <Route path="/founder/ads" element={<FounderAdConsole />} />
          <Route path="*" element={<AppShell />} />
        </Routes>
        <MiniPlayer />
        <TopUpGate />
        <InstallAppPrompt />
        <RenderDock />
      </div>

      {/* Desktop-only System Analytics & Control Dashboard (>=1024px) —
          restricted to admins / Tier-3 founders, shown when the active
          workspace pane is the CONTROL_DECK (Home). */}
      {deckVisible && <DesktopDashboard />}
      {centreVisible && <UserCommandCentre />}
    </>
  );
}

function App() {
  return (
    <div className="App min-h-[100dvh] w-full flex justify-center md:block bg-neutral-950 relative" data-testid="app-root">
      <div className="noise-overlay" aria-hidden="true" />
      <RateLimitBanner />
      <SessionProvider>
        <BillingGateProvider>
          <PlayerProvider>
            <WorkspaceProvider>
              <BrowserRouter>
                <RootShell />
              </BrowserRouter>
            </WorkspaceProvider>
          </PlayerProvider>
        </BillingGateProvider>
      </SessionProvider>
      <Toaster
        position="top-center"
        offset={72}
        mobileOffset={72}
        duration={2500}
        toastOptions={{
          className: "!w-[320px] !max-w-[86vw] !mx-auto",
          style: {
            background: "#1A1A1A",
            color: "#FDFBF7",
            border: "2px solid #D4AF37",
            fontFamily: "JetBrains Mono, monospace",
            borderRadius: "16px",
          },
        }}
      />
    </div>
  );
}

export default App;
